import {
  AttendanceCode,
  AttendanceDetail,
  AttendanceEntriesCounts,
  AttendanceFrequencyResponse,
  AttendanceRegisterStatisticsResponse,
  AttendanceSessionEnum,
  FrequencyTypeEnum,
  JustificationBase,
  JustificationCounts,
  JustificationSave,
  JustificationStatus,
  ResponsibleTypeEnum,
} from '@schooly/api';
import { JustificationChangeType } from '@schooly/components/justifications';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { isAfter, isWithinInterval } from 'date-fns';

const DEFAULT_PRECISION_FOR_PERCENT = 1;

export type AttendanceSessionState = 'completed' | 'required' | 'subject_teacher';

export interface AttendanceFrequencyConfig {
  sessions: AttendanceSessionEnum[];
  isSubjectResponsible: Record<AttendanceSessionEnum, boolean>;
}

const SESSIONS_BY_FREQUENCY: Record<FrequencyTypeEnum, AttendanceSessionEnum[]> = {
  [FrequencyTypeEnum.Am]: [AttendanceSessionEnum.Am],
  [FrequencyTypeEnum.Pm]: [AttendanceSessionEnum.Pm],
  [FrequencyTypeEnum.AmPm]: [AttendanceSessionEnum.Am, AttendanceSessionEnum.Pm],
};

export const getAttendanceFrequencyConfig = (
  frequency?: AttendanceFrequencyResponse | null,
): AttendanceFrequencyConfig => ({
  sessions: frequency ? SESSIONS_BY_FREQUENCY[frequency.frequency] : [AttendanceSessionEnum.Am],
  isSubjectResponsible: {
    [AttendanceSessionEnum.Am]: frequency?.am_responsible === ResponsibleTypeEnum.SubjectTeacher,
    [AttendanceSessionEnum.Pm]: frequency?.pm_responsible === ResponsibleTypeEnum.SubjectTeacher,
  },
});

type ResolveAttendanceSessionStateParams = {
  isTutorGroup: boolean;
  sessionExists: boolean;
  isCompleted: boolean;
  isSubjectResponsible: boolean;
};

export const resolveAttendanceSessionState = ({
  isTutorGroup,
  sessionExists,
  isCompleted,
  isSubjectResponsible,
}: ResolveAttendanceSessionStateParams): AttendanceSessionState | null => {
  if (isTutorGroup) {
    if (isCompleted) return 'completed';
    if (isSubjectResponsible) return 'subject_teacher';
    if (sessionExists) return 'required';

    return null;
  }

  if (!isSubjectResponsible || !sessionExists) return null;

  return isCompleted ? 'completed' : 'required';
};

export function getAttendanceDetailByCode(
  code: AttendanceCode,
  attendanceDetails?: AttendanceDetail[],
) {
  return attendanceDetails?.find((detail) => detail.attendance_code_id === code.id);
}

type AttendanceCount = Pick<AttendanceDetail, 'attendance_code_id' | 'count'>;

const roundPercent = (value: number, precision: number) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

export const getAttendancePercentages = (present: number, absent: number) => {
  const total = present + absent;

  if (!total) {
    return { present: 0, absent: 0 };
  }

  const presentPercent = roundPercent((present / total) * 100, DEFAULT_PRECISION_FOR_PERCENT);
  const absentPercent = roundPercent(100 - presentPercent, DEFAULT_PRECISION_FOR_PERCENT);

  return { present: presentPercent, absent: absentPercent };
};

export function getAttendanceDetailsByCodes(codes: string[], attendanceDetails: AttendanceCount[]) {
  return attendanceDetails.filter(
    (detail) =>
      detail.attendance_code_id &&
      !!codes.find((code) => code === detail.attendance_code_id)?.length,
  );
}

export function getNoStatusTotal(attendanceDetails?: AttendanceCount[]): number {
  return (
    attendanceDetails?.reduce(
      (sum, detail) => (!detail.attendance_code_id ? sum + detail.count : sum),
      0,
    ) ?? 0
  );
}

export const getCodesById = (codes: AttendanceCode[]): Record<string, AttendanceCode> =>
  codes.reduce<Record<string, AttendanceCode>>((acc, code) => {
    acc[code.id] = code;
    return acc;
  }, {});

export const getPresentAbsentCodeIds = (
  codes: AttendanceCode[],
): { present: string[]; absent: string[] } => {
  return codes.reduce<{ present: string[]; absent: string[] }>(
    (acc, code) => {
      if (code.in_school || code.is_present) {
        acc.present.push(code.id);
      } else {
        acc.absent.push(code.id);
      }
      return acc;
    },
    { present: [], absent: [] },
  );
};

export function getTotalByCodeIds(codeIds: string[], attendanceDetails: AttendanceCount[]): number {
  const attendanceDetailsByCodes = getAttendanceDetailsByCodes(codeIds, attendanceDetails);

  return attendanceDetailsByCodes.reduce((sum, detail) => {
    return sum + detail.count;
  }, 0);
}

export const isTodayInPeriod = (date: [string, string]) => {
  const start = newDateTimezoneOffset(date[0]);
  const end = newDateTimezoneOffset(date[1]);

  if (isAfter(start, end)) return false;
  return isWithinInterval(new Date(), { start, end });
};

export const hasJustificationCounts = (justificationCounts: Partial<JustificationCounts>) => {
  return Object.values(justificationCounts).some((count) => count > 0);
};

export const hasNotification = (code: AttendanceCode, notificationsEnabled: boolean) =>
  code.has_justification || (notificationsEnabled && code.has_push_notifications);

type ShouldConfirmJustificationParams = {
  newCode: AttendanceCode;
  hasJustification: boolean;
  hasAbsenceNotice: boolean;
};

export const shouldConfirmJustification = ({
  newCode,
  hasJustification,
  hasAbsenceNotice,
}: ShouldConfirmJustificationParams) => {
  return newCode.has_justification && !hasJustification && hasAbsenceNotice;
};

type JustificationChangeParams = {
  status: JustificationStatus;
  newCode: AttendanceCode;
  codes: AttendanceCode[];
  entries: Array<{ session: AttendanceSessionEnum; attendance_code_id: string | null }>;
  sessions: AttendanceSessionEnum[];
};

const findCodes = (codes: AttendanceCode[], codeIds: Array<string | null>) =>
  codeIds.reduce<AttendanceCode[]>((acc, id) => {
    const code = id ? codes.find((item) => item.id === id) : undefined;
    if (code) acc.push(code);
    return acc;
  }, []);

const getResultingCodes = ({ newCode, codes, entries, sessions }: JustificationChangeParams) =>
  findCodes(
    codes,
    entries.map((entry) =>
      sessions.includes(entry.session) ? newCode.id : entry.attendance_code_id,
    ),
  );

export const getJustificationChangeType = (
  params: JustificationChangeParams,
): JustificationChangeType | undefined => {
  const resultingCodes = getResultingCodes(params);
  const keepsJustification = resultingCodes.some((code) => code.has_justification);

  if (params.status === JustificationStatus.Closed) {
    return keepsJustification ? 'reopen' : undefined;
  }

  if (keepsJustification) return undefined;

  if (params.status === JustificationStatus.Expired) return 'close';

  if (params.status === JustificationStatus.Pending) {
    return resultingCodes.some((code) => !code.is_present) ? 'close' : undefined;
  }

  return undefined;
};

type ResultingJustificationParams = {
  justification: JustificationBase | null;
  note: JustificationSave | null;
  codes: AttendanceCode[];
  entries: Array<{ attendance_code_id: string | null }>;
};

const PENDING_JUSTIFICATION: JustificationBase = {
  id: '',
  status: JustificationStatus.Pending,
  parent_submissions: [],
  staff_reasons: [],
  staff_requests: [],
};

export const getResultingJustification = ({
  justification,
  note,
  codes,
  entries,
}: ResultingJustificationParams): JustificationBase | null => {
  const markedCodes = findCodes(
    codes,
    entries.map((entry) => entry.attendance_code_id),
  );
  const needsJustification = markedCodes.some((code) => code.has_justification);

  if (!justification) return needsJustification ? PENDING_JUSTIFICATION : null;

  if (note && 'staff_request' in note) {
    return { ...justification, status: JustificationStatus.Pending };
  }

  if (!markedCodes.length || needsJustification) return justification;

  return { ...justification, status: JustificationStatus.Closed };
};

export const getResultingJustificationStatus = (
  params: ResultingJustificationParams,
): JustificationStatus | undefined => {
  const nextStatus = getResultingJustification(params)?.status;
  if (!nextStatus || nextStatus === params.justification?.status) return undefined;

  return nextStatus;
};

export const hasJustificationNote = (
  note: JustificationSave,
  changeType: JustificationChangeType,
) => {
  return changeType === 'close' ? 'staff_reason' in note : 'staff_request' in note;
};

type RequiresJustificationConfirmParams = Omit<JustificationChangeParams, 'status'> & {
  status?: JustificationStatus;
  hasAbsenceNotice: boolean;
};

export const requiresJustificationConfirm = ({
  newCode,
  hasAbsenceNotice,
  status,
  codes,
  entries,
  sessions,
}: RequiresJustificationConfirmParams) => {
  if (shouldConfirmJustification({ newCode, hasJustification: !!status, hasAbsenceNotice })) {
    return true;
  }

  if (!status) return false;

  return !!getJustificationChangeType({ status, newCode, codes, entries, sessions });
};

export const getJustificationCounts = (countsList: JustificationCounts[]): JustificationCounts => {
  return countsList.reduce(
    (acc, counts) => ({
      pending: acc.pending + counts.pending,
      submitted: acc.submitted + counts.submitted,
      expired: acc.expired + counts.expired,
      closed: acc.closed + counts.closed,
    }),
    { pending: 0, submitted: 0, expired: 0, closed: 0 },
  );
};

export type AttendanceAgeGroupSummary = {
  id: string;
  name: string;
  order: number;
  attendanceDetails: AttendanceEntriesCounts[];
  justificationCounts: JustificationCounts;
};

const NO_STATUS_CODE_KEY = 'no_status';

export const aggregateStatisticsByAgeGroup = (
  statistics: AttendanceRegisterStatisticsResponse,
): AttendanceAgeGroupSummary[] =>
  [...statistics.age_group_entries]
    .sort((a, b) => a.age_group.order - b.age_group.order)
    .map(({ age_group, sessions, justification_counts }) => {
      const detailsMap = new Map<string, AttendanceEntriesCounts>();
      for (const session of sessions) {
        for (const detail of session.attendance_details) {
          const key = detail.attendance_code_id ?? NO_STATUS_CODE_KEY;
          const existing = detailsMap.get(key);
          if (existing) {
            existing.count += detail.count;
          } else {
            detailsMap.set(key, { ...detail });
          }
        }
      }
      return {
        id: age_group.id,
        name: age_group.name,
        order: age_group.order,
        attendanceDetails: [...detailsMap.values()],
        justificationCounts: justification_counts,
      };
    });

export const getRegisterTotalJustificationCounts = (
  countsList: JustificationCounts[],
): JustificationCounts => {
  return getJustificationCounts(countsList);
};
